import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, AlertTriangle, Check, ChevronDown, ChevronUp } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20, scale: 0.95 },
  visible: { opacity: 1, x: 0, scale: 1, transition: { type: 'spring', damping: 20, stiffness: 200 } },
  exit: { opacity: 0, x: -20, scale: 0.95, transition: { duration: 0.15 } },
};

export default function LowStockAlert({ alerts = [], onResolve, onViewAll }) {
  const [collapsed, setCollapsed] = useState(false);
  const [resolvedIds, setResolvedIds] = useState([]);

  const activeAlerts = alerts.filter((a) => !resolvedIds.includes(a.id || a._id));
  const criticalAlerts = activeAlerts.filter((a) => (a.stock || a.quantity || 0) <= 0);
  const lowAlerts = activeAlerts.filter((a) => (a.stock || a.quantity || 0) > 0);

  const handleResolve = (alert) => {
    setResolvedIds((prev) => [...prev, alert.id || alert._id]);
    onResolve?.(alert);
  };

  if (alerts.length === 0) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <AlertTriangle size={18} className="text-amber-500" />
            {activeAlerts.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full text-[9px] font-bold text-white flex items-center justify-center">
                {activeAlerts.length}
              </span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Low Stock Alerts</h3>
        </div>
        {collapsed ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronUp size={16} className="text-gray-400" />}
      </button>

      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 space-y-2">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-2"
              >
                {activeAlerts.length === 0 ? (
                  <motion.div
                    variants={itemVariants}
                    className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg"
                  >
                    <Check size={16} className="text-emerald-500" />
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">All stock levels are good</p>
                  </motion.div>
                ) : (
                  <>
                    {criticalAlerts.map((alert, idx) => (
                      <motion.div
                        key={alert.id || alert._id || `critical-${idx}`}
                        variants={itemVariants}
                        layout
                        className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800/30"
                      >
                        <div className="w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                          <Package size={16} className="text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{alert.name}</p>
                          <p className="text-xs text-red-500 dark:text-red-400 font-medium">
                            Out of stock
                          </p>
                        </div>
                        <button
                          onClick={() => handleResolve(alert)}
                          className="p-1.5 rounded-lg bg-white dark:bg-gray-800 text-gray-400 hover:text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors shadow-sm"
                        >
                          <Check size={14} />
                        </button>
                      </motion.div>
                    ))}

                    {lowAlerts.map((alert, idx) => {
                      const minStock = alert.minStock || 5;
                      const stock = alert.stock || alert.quantity || 0;
                      const severity = stock <= minStock / 2 ? 'high' : 'low';
                      return (
                        <motion.div
                          key={alert.id || alert._id || `low-${idx}`}
                          variants={itemVariants}
                          layout
                          className={`flex items-center gap-3 p-3 rounded-lg border ${
                            severity === 'high'
                              ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/30'
                              : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/30'
                          }`}
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            severity === 'high'
                              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-500'
                              : 'bg-orange-100 dark:bg-orange-900/30 text-orange-500'
                          }`}>
                            <Package size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{alert.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Stock: <span className="font-medium text-amber-600 dark:text-amber-400">{stock}</span> / Min: {minStock}
                            </p>
                          </div>
                          <button
                            onClick={() => handleResolve(alert)}
                            className="p-1.5 rounded-lg bg-white dark:bg-gray-800 text-gray-400 hover:text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors shadow-sm"
                          >
                            <Check size={14} />
                          </button>
                        </motion.div>
                      );
                    })}
                  </>
                )}
              </motion.div>

              {onViewAll && activeAlerts.length > 0 && (
                <button
                  onClick={onViewAll}
                  className="w-full py-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                >
                  View all alerts
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
